// src/utils/usePageView.js
// Hook that wraps trackPageView and auto-captures navigation context from
// React Router. Use this in any page component that fires a single page-view
// event on mount. For pages with dynamic page names (e.g. LessonPage's
// intro / age-picker / lesson screens), call trackPageView imperatively
// and pass the metadata returned by `useNavMeta`.
//
// Captured nav context (attached as metadata on the page_view event):
//   • navAction:    'POP' | 'PUSH' | 'REPLACE'
//                   POP on initial cold land AND on browser back/forward.
//                   PUSH on navigate(...) calls.
//                   REPLACE on navigate(..., { replace: true }) and the
//                   catch-all <Navigate replace />.
//   • isInitialLoad true on the very first page in this React Router
//                   session (location.key === 'default'). Combined with
//                   navAction, lets the dashboard distinguish:
//                     - cold ad land  : POP + isInitialLoad
//                     - browser back  : POP + !isInitialLoad
//                     - in-app click  : PUSH
//                     - redirect      : REPLACE
//   • navSource     Optional explicit label set by the previous page when
//                   it called navigate(..., { state: { navSource: '...' }}).
//                   Examples: 'home_cta_free_trial', 'trial_intro_back_arrow',
//                   'footer_link'. null when not set.
//
// We intentionally do NOT track prevPath here — the admin dashboard already
// shows events in chronological order within a session, so the previous
// event in the timeline IS effectively the prev path. Tracking it
// separately would be redundant and introduce timing bugs.
import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { trackPageView } from './analyticsTracker';

/**
 * Compute the navigation context for the current location. Use this when
 * you need to call trackPageView imperatively (e.g. inside a useEffect
 * with state-dependent page names like LessonPage). Returns a stable
 * snapshot of the current navigation — captured at the moment of the call.
 */
export const useNavMeta = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  return {
    navAction: navigationType, // 'POP' | 'PUSH' | 'REPLACE'
    isInitialLoad: location.key === 'default',
    navSource: location.state?.navSource || null,
  };
};

/**
 * Fire a single trackPageView on mount with nav context auto-attached.
 * For pages with a fixed page name. The dedup in trackPageView ensures
 * only one page_view event per page name per session, regardless of how
 * many times the component remounts.
 */
export const usePageView = (pageName, options = {}) => {
  const { userId = null, extraMeta = null } = options;
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    trackPageView(pageName, userId, {
      navAction: navigationType,
      isInitialLoad: location.key === 'default',
      navSource: location.state?.navSource || null,
      ...(extraMeta || {}),
    });
    // Only run on mount. The page name is fixed for a given component
    // instance, and trackPageView dedupes anyway. Re-running on location
    // changes would be a no-op (still deduped) but is noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
